declare global {
  interface Window {
    google?: {
      maps?: typeof google.maps;
    };
  }
}

/**
 * Signals that the Google Maps Geocoder is not available yet. The Maps JS API
 * only loads once the user opens the Google map, so reverse geocoding can be
 * requested before a Geocoder exists (or while the API is only partially
 * loaded). This is an expected condition, not a failure, so callers should
 * treat it as non-fatal and must NOT report it to Sentry — doing so was the
 * source of tens of thousands of noise events per week.
 */
export class MapsNotReadyError extends Error {
  constructor(
    message = 'Google Maps JavaScript API is not loaded. Cannot reverse geocode.',
  ) {
    super(message);
    this.name = 'MapsNotReadyError';
  }
}

/**
 * Returns true once the Google Maps JS API and its Geocoder constructor are
 * both available. Checking the Geocoder specifically matters: the API can be
 * partially present (`window.google.maps` exists) before the Geocoder class is
 * registered, which previously surfaced as the runtime error
 * "window.google.maps.Geocoder is not a constructor".
 */
export const isGoogleGeocoderReady = (): boolean =>
  typeof window.google?.maps?.Geocoder === 'function';
